import React, { useEffect, useState } from "react";
import Clarity from "@microsoft/clarity";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  alpha,
  Avatar,
  Box,
  Collapse,
  Divider,
  IconButton,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import {
  Check,
  Close,
  DarkModeOutlined,
  ExpandLess,
  ExpandMore,
  LightModeOutlined,
  LogoutRounded,
  MenuRounded,
  SettingsOutlined,
  TranslateOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { NAVIGATION_EVENTS, USER_EVENTS } from "../../utils/clarityEvents";
import { logout } from "../../store/slices/session/sessionSlice";
import {
  selectAvatar,
  selectUser,
  selectUserDb,
} from "../../store/slices/session/sessionSelector";
import {
  selectLanguage,
  selectSidebarCollapsed,
} from "../../store/slices/app/appSelector";
import { LanguageEnum } from "../../store/slices/app/appTypes";
import {
  clearSimulatedRole,
  setLanguage,
  toggleSidebar,
} from "../../store/slices/app/appSlice";
import { getTenantLogo, useTenantBranding } from "../../utils/tenantBranding";
import { useThemeMode } from "../../theme/ThemeModeProvider";
import { useUserRole } from "../../hooks/useUserRole";
import type { ClientPortalCompany } from "../../store/apis/clientPortalApi";
import { useClientCompany } from "../client/useClientCompanyViewClient";
import { CampaignIcon } from "../../icons/customIcons";
import {
  campaignPath,
  CLIENT_SIDEBAR_WIDTH,
  COLLAPSED_SIDEBAR_WIDTH,
  subcampaignPath,
} from "../client/clientFormatViewClient";
import { ELECTRIC_BLUE, StatusMark } from "../client/ClientUiViewClient";
import {
  TITLE_SOURCE_ATTR,
  transitionLinkProps,
  useClientTransition,
} from "../client/clientTransitionViewClient";

// Del ancho del texto: el nombre viaja hasta el título sin deformarse.
const nameFit = { width: "fit-content", maxWidth: "100%" } as const;

const LANGUAGE_OPTIONS: { value: LanguageEnum; label: string }[] = [
  { value: LanguageEnum.ES, label: "Español" },
  { value: LanguageEnum.EN, label: "English" },
];

const SectionLabel: React.FC<{
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ children, icon }) => (
  <Typography
    sx={{
      px: 3,
      pt: 3.5,
      pb: 1.25,
      display: "flex",
      alignItems: "center",
      gap: 1,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "text.secondary",
    }}
  >
    {icon}
    {children}
  </Typography>
);

const CompanyAvatar: React.FC<{ company: ClientPortalCompany }> = ({
  company,
}) => (
  <Avatar
    src={company.logo ?? undefined}
    alt=""
    aria-hidden
    variant="rounded"
    sx={{
      width: 32,
      height: 32,
      borderRadius: "10px",
      fontSize: 14,
      fontWeight: 700,
      bgcolor: "neutral.200",
      color: "text.primary",
      border: 1,
      borderColor: "divider",
      "& img": { objectFit: "contain" },
    }}
  >
    {company.name.charAt(0).toUpperCase()}
  </Avatar>
);

const CompanyName: React.FC<{ name: string; selected?: boolean }> = ({
  name,
  selected = true,
}) => (
  <Typography
    title={name}
    sx={{
      minWidth: 0,
      fontSize: 15,
      fontWeight: selected ? 700 : 500,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {name}
  </Typography>
);

// Brillo del logo: el degradado entra por la izquierda y sale por la derecha.
const logoShine = keyframes`
  from { background-position: 100% 0; }
  to { background-position: 0% 0; }
`;

/**
 * Sidebar del cliente: el logo (que lleva al inicio), la empresa (con un
 * selector si tiene varias) y sus campañas. La campaña abierta despliega sus
 * subcampañas. El modo oscuro, el idioma y la salida están en el menú del
 * usuario.
 */
const SidebarViewClient: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const { mode, toggleMode } = useThemeMode();
  const branding = useTenantBranding();
  const logoSrc = getTenantLogo(branding, mode === "dark" ? "white" : "default");
  const currentUser = useSelector(selectUser);
  const userDb = useSelector(selectUserDb);
  const avatar = useSelector(selectAvatar);
  const language = useSelector(selectLanguage);
  const { role, isSimulating } = useUserRole();
  const navigate = useNavigate();
  const { data, companies, company, campaigns, selectCompany } =
    useClientCompany();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [companyAnchor, setCompanyAnchor] = useState<HTMLElement | null>(null);
  const [languageOpen, setLanguageOpen] = useState(false);

  // Se pliega con la X y se despliega con la hamburguesa. Comparte el estado
  // con el sidebar general, así que todas las páginas dejan el margen bueno.
  const collapsed = useSelector(selectSidebarCollapsed);
  const toggle = () => dispatch(toggleSidebar());

  const canSwitch = companies.length > 1;
  const pathMatch = location.pathname.match(
    /^(?:\/client)?\/campaigns\/([^/]+)(?:\/subcampaigns\/([^/]+))?$/
  );
  const openCampaignId = pathMatch?.[1];
  const openSubId = pathMatch?.[2];

  // Transiciones: un enlace se abre hasta ser su página; el inicio cierra la
  // página abierta en su fila.
  const go = useClientTransition();
  const homeLink = transitionLinkProps(go, "/", {
    returnTo: openSubId ?? openCampaignId,
  });

  // Al abrir una campaña de otra empresa (un enlace, el historial) se pasa a
  // esa empresa, para que el sidebar enseñe la campaña abierta.
  const openCompanyId = data?.campaigns.find(
    (c) => c.id === openCampaignId
  )?.companyId;
  useEffect(() => {
    if (openCompanyId) selectCompany(openCompanyId);
  }, [openCompanyId, selectCompany]);

  // Al cambiar de empresa con una campaña de la otra abierta, se vuelve al
  // inicio de la nueva.
  const chooseCompany = (companyId: string) => {
    setCompanyAnchor(null);
    if (companyId === company?.id) return;
    selectCompany(companyId);
    if (openCompanyId && openCompanyId !== companyId) navigate("/");
  };

  const fullName = userDb
    ? `${userDb.firstName} ${userDb.lastName}`
    : t("sidebar.defaultUserName");

  const closeMenu = () => {
    setMenuAnchor(null);
    Clarity.event(USER_EVENTS.PROFILE_MENU_CLOSE);
  };

  const itemSx = (active: boolean) => ({
    mx: 1.5,
    px: 1.5,
    py: 1,
    borderRadius: "12px",
    gap: 1.5,
    color: "text.primary",
    backgroundColor: active
      ? alpha(theme.palette.text.primary, 0.08)
      : "transparent",
    "&:hover": {
      backgroundColor: alpha(theme.palette.text.primary, active ? 0.1 : 0.05),
    },
  });

  const menuItemSx = {
    borderRadius: 2,
    p: 1,
    gap: 1,
    alignItems: "center",
    "&:hover": { backgroundColor: theme.palette.neutral[200] },
  };

  const openUserMenu = (target: HTMLElement) => {
    setMenuAnchor(target);
    Clarity.event(USER_EVENTS.PROFILE_MENU_OPEN);
  };

  return (
    <Box
      component="nav"
      sx={{
        width: collapsed ? COLLAPSED_SIDEBAR_WIDTH : CLIENT_SIDEBAR_WIDTH,
        transition: "width 0.2s ease",
        overflow: "hidden",
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        // Gris del tema con un toque del azul eléctrico que se difumina enseguida.
        backgroundColor: theme.palette.neutral[100],
        backgroundImage: `linear-gradient(90deg, ${alpha(
          ELECTRIC_BLUE[mode],
          mode === "dark" ? 0.4 : 0.28
        )} 0, ${alpha(ELECTRIC_BLUE[mode], 0)} 40px)`,
        borderRight: `1px solid ${theme.palette.divider}`,
        "@media print": { display: "none" },
      }}
    >
      {/* Plegado: solo la hamburguesa para desplegarlo y el usuario abajo. */}
      {collapsed && (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
            <IconButton
              onClick={toggle}
              aria-label={t("clientView.sidebar.expand")}
              sx={{ color: "text.primary" }}
            >
              <MenuRounded />
            </IconButton>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
        </>
      )}

      {/* El logo es el botón de inicio; la X pliega el sidebar. */}
      {!collapsed && (
        <Box
          sx={{
            pl: 3,
            pr: 1.5,
            pt: 3,
            pb: 1,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            aria-label={t("clientView.home")}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              Clarity.event(NAVIGATION_EVENTS.HOME_TAB_CLICK);
              // La página abierta se cierra en su fila del inicio.
              homeLink.onClick(event);
            }}
            sx={{
              display: "inline-flex",
              py: 0.5,
              minWidth: 0,
              flex: "0 1 auto",
              "& .logo-frame": {
                transition: "transform 0.25s ease",
                transformOrigin: "left center",
              },
              "&:hover .logo-frame, &:focus-visible .logo-frame": {
                transform: "scale(1.04)",
              },
              "&:hover .logo-shine, &:focus-visible .logo-shine": {
                animation: `${logoShine} 0.9s ease-out`,
              },
              "@media (prefers-reduced-motion: reduce)": {
                "&:hover .logo-frame, &:focus-visible .logo-frame": {
                  transform: "none",
                },
                "&:hover .logo-shine, &:focus-visible .logo-shine": {
                  animation: "none",
                },
              },
            }}
          >
            <Box
              className="logo-frame"
              sx={{ position: "relative", display: "flex", minWidth: 0 }}
            >
              {/* Si el logo es ancho se encoge para no pisar la X. */}
              <Box
                component="img"
                src={logoSrc}
                alt={t("sidebar.logoAlt", { name: branding.displayName })}
                sx={{
                  display: "block",
                  height: 24,
                  width: "auto",
                  maxWidth: "100%",
                  objectFit: "contain",
                  objectPosition: "left center",
                }}
              />
              {/* El brillo usa el propio logo de máscara: solo pasa por las
                  letras, nunca por el fondo. En reposo queda fuera del logo. */}
              <Box
                className="logo-shine"
                aria-hidden
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  backgroundImage: `linear-gradient(115deg, transparent 35%, ${alpha(
                    ELECTRIC_BLUE[mode],
                    0.9
                  )} 45%, ${mode === "dark" ? "#b9ccff" : "#7fa3ff"} 50%, ${alpha(
                    ELECTRIC_BLUE[mode],
                    0.9
                  )} 55%, transparent 65%)`,
                  backgroundSize: "300% 100%",
                  backgroundPosition: "100% 0",
                  backgroundRepeat: "no-repeat",
                  maskImage: `url("${logoSrc}")`,
                  maskSize: "contain",
                  maskPosition: "left center",
                  maskRepeat: "no-repeat",
                  WebkitMaskImage: `url("${logoSrc}")`,
                  WebkitMaskSize: "contain",
                  WebkitMaskPosition: "left center",
                  WebkitMaskRepeat: "no-repeat",
                }}
              />
            </Box>
          </Box>
          <IconButton
            onClick={toggle}
            aria-label={t("clientView.sidebar.collapse")}
            size="small"
            sx={{ color: "text.secondary", flexShrink: 0 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      {!collapsed && (
        <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", pb: 2 }}>
          {company && (
            <>
              <SectionLabel>
                {t("clientView.sidebar.companies", { count: 1 })}
              </SectionLabel>
              {/* Con una sola empresa es solo su nombre; con más, un selector. */}
              {canSwitch ? (
                <ListItemButton
                  onClick={(event) => setCompanyAnchor(event.currentTarget)}
                  aria-haspopup="listbox"
                  aria-expanded={Boolean(companyAnchor)}
                  aria-label={t("clientView.sidebar.switchCompany")}
                  sx={itemSx(Boolean(companyAnchor))}
                >
                  <CompanyAvatar company={company} />
                  <CompanyName name={company.name} />
                  <ExpandMore
                    sx={{
                      ml: "auto",
                      fontSize: 20,
                      color: "text.secondary",
                      transition: "transform 150ms",
                      transform: companyAnchor ? "rotate(180deg)" : "none",
                    }}
                  />
                </ListItemButton>
              ) : (
                <Box
                  sx={{
                    ...itemSx(false),
                    display: "flex",
                    alignItems: "center",
                    "&:hover": {},
                  }}
                >
                  <CompanyAvatar company={company} />
                  <CompanyName name={company.name} />
                </Box>
              )}
            </>
          )}

          <SectionLabel icon={<CampaignIcon color="currentColor" size={16} />}>
            {t("clientView.sidebar.campaigns")}
          </SectionLabel>
          {data && campaigns.length === 0 && (
            <Typography sx={{ px: 3, fontSize: 14, color: "text.secondary" }}>
              {t("clientView.sidebar.noCampaigns")}
            </Typography>
          )}
          {campaigns.map((campaign) => {
            const isOpen = campaign.id === openCampaignId;
            return (
              <React.Fragment key={campaign.id}>
                <ListItemButton
                  component={RouterLink}
                  to={campaignPath(campaign.id)}
                  {...transitionLinkProps(go, campaignPath(campaign.id), {
                    expand: true,
                  })}
                  sx={itemSx(isOpen && !openSubId)}
                >
                  <StatusMark status={campaign.status} />
                  <ListItemText
                    primary={campaign.name}
                    primaryTypographyProps={{
                      noWrap: true,
                      title: campaign.name,
                      ...{ [TITLE_SOURCE_ATTR]: "" },
                      sx: {
                        ...nameFit,
                        fontSize: 14,
                        fontWeight: isOpen ? 700 : 500,
                      },
                    }}
                  />
                </ListItemButton>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      ml: "37px",
                      mr: 1.5,
                      my: 0.5,
                      pl: 1,
                      borderLeft: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    {campaign.subcampaigns.map((sub) => (
                      <ListItemButton
                        key={sub.id}
                        component={RouterLink}
                        to={subcampaignPath(campaign.id, sub.id)}
                        {...transitionLinkProps(
                          go,
                          subcampaignPath(campaign.id, sub.id),
                          { expand: true }
                        )}
                        sx={{
                          ...itemSx(sub.id === openSubId),
                          mx: 0,
                          py: 0.75,
                        }}
                      >
                        <ListItemText
                          primary={sub.name}
                          primaryTypographyProps={{
                            noWrap: true,
                            title: sub.name,
                            ...{ [TITLE_SOURCE_ATTR]: "" },
                            sx: {
                              ...nameFit,
                              fontSize: 13,
                              fontWeight: sub.id === openSubId ? 700 : 400,
                              color:
                                sub.id === openSubId
                                  ? "text.primary"
                                  : "text.secondary",
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </Box>
                </Collapse>
              </React.Fragment>
            );
          })}
        </Box>
      )}

      <Popover
        open={Boolean(companyAnchor)}
        anchorEl={companyAnchor}
        onClose={() => setCompanyAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            role: "listbox",
            sx: {
              width: CLIENT_SIDEBAR_WIDTH - 24,
              mt: 0.75,
              p: 1,
              borderRadius: "16px",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 12px 32px rgba(0,0,0,0.10)",
            },
          },
        }}
      >
        {companies.map((option) => {
          const selected = option.id === company?.id;
          return (
            <ListItemButton
              key={option.id}
              role="option"
              aria-selected={selected}
              onClick={() => chooseCompany(option.id)}
              sx={{ ...menuItemSx, gap: 1.5, py: 1 }}
            >
              <CompanyAvatar company={option} />
              <CompanyName name={option.name} selected={selected} />
              {selected && (
                <Check
                  sx={{ ml: "auto", fontSize: 18, color: "primary.main" }}
                />
              )}
            </ListItemButton>
          );
        })}
      </Popover>

      <Divider />

      {/* Usuario: abre el menú con el modo oscuro, el idioma y la salida. */}
      <Box
        role="button"
        tabIndex={0}
        aria-label={collapsed ? fullName : undefined}
        onClick={(event) => openUserMenu(event.currentTarget)}
        onKeyDown={(event) => {
          if (event.key === "Enter") setMenuAnchor(event.currentTarget);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 1.5,
          px: collapsed ? 0 : 3,
          py: 2.5,
          cursor: "pointer",
          flexShrink: 0,
          "&:hover": {
            backgroundColor: alpha(theme.palette.text.primary, 0.05),
          },
        }}
      >
        <Avatar src={avatar || undefined} sx={{ width: 36, height: 36 }}>
          {fullName.charAt(0)}
        </Avatar>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 700 }}>
              {fullName}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, color: "text.secondary" }}>
              {currentUser?.email || t("sidebar.noEmail")}
            </Typography>
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: CLIENT_SIDEBAR_WIDTH - 24,
              ml: 1.5,
              mt: -1,
              p: 1,
              borderRadius: "16px",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 12px 32px rgba(0,0,0,0.10)",
            },
          },
        }}
      >
        {isSimulating && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.warning.main, 0.15),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VisibilityOutlined sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                {t("sidebar.viewingAsRole", "Viendo como")}: {role}
              </Typography>
            </Box>
            <ListItemButton
              onClick={() => {
                dispatch(clearSimulatedRole());
                closeMenu();
              }}
              aria-label={t("sidebar.stopSimulating", "Volver a mi rol real")}
              sx={{ flexGrow: 0, p: 0.25, borderRadius: 1 }}
            >
              <Close sx={{ fontSize: 16 }} />
            </ListItemButton>
          </Box>
        )}

        <ListItemButton onClick={toggleMode} sx={menuItemSx}>
          {mode === "dark" ? (
            <LightModeOutlined sx={{ fontSize: 18 }} />
          ) : (
            <DarkModeOutlined sx={{ fontSize: 18 }} />
          )}
          <ListItemText
            primary={
              mode === "dark"
                ? t("sidebar.lightMode", "Light mode")
                : t("sidebar.darkMode", "Dark mode")
            }
          />
        </ListItemButton>

        <ListItemButton
          onClick={() => setLanguageOpen((prev) => !prev)}
          sx={menuItemSx}
        >
          <TranslateOutlined sx={{ fontSize: 18 }} />
          <ListItemText primary={t("sidebar.language", "Idioma")} />
          {languageOpen ? (
            <ExpandLess sx={{ fontSize: 18 }} />
          ) : (
            <ExpandMore sx={{ fontSize: 18 }} />
          )}
        </ListItemButton>
        <Collapse in={languageOpen} timeout="auto" unmountOnExit>
          {LANGUAGE_OPTIONS.map(({ value, label }) => (
            <ListItemButton
              key={value}
              selected={(language || LanguageEnum.EN) === value}
              onClick={() => {
                dispatch(setLanguage(value));
                setLanguageOpen(false);
              }}
              sx={{ ...menuItemSx, pl: 4.5 }}
            >
              <ListItemText primary={label} />
            </ListItemButton>
          ))}
        </Collapse>

        <ListItemButton
          component={RouterLink}
          to="/settings"
          onClick={() => {
            closeMenu();
            Clarity.event(NAVIGATION_EVENTS.SETTINGS_NAVIGATION);
          }}
          sx={menuItemSx}
        >
          <SettingsOutlined sx={{ fontSize: 18 }} />
          <ListItemText primary={t("sidebar.settings")} />
        </ListItemButton>

        <Divider sx={{ my: 0.5 }} />

        <ListItemButton
          onClick={() => {
            closeMenu();
            Clarity.event(USER_EVENTS.USER_LOGOUT);
            dispatch(logout());
          }}
          sx={{
            ...menuItemSx,
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.12),
            },
          }}
        >
          <LogoutRounded sx={{ fontSize: 18 }} />
          <ListItemText primary={t("sidebar.logout")} />
        </ListItemButton>
      </Popover>
    </Box>
  );
};

export default SidebarViewClient;
